/**
 * Reading a state or province out of the same free-text location the country
 * comes from, for Canada and the United States.
 *
 * The owner, 2026-10-22: "make sure there is a state level filter as well in
 * addition to country level filter for contacts". A country filter cannot pick
 * Alberta out of Canada, and Alberta is the market the Wixy soap list is for.
 *
 * Stored as the postal code of the province or state ("AB", "TX"), which is the
 * second half of its ISO 3166-2 code. The two tables share no code, so a code
 * alone names one place.
 *
 * Same rule as the country: guessing is worse than not knowing. A full name
 * ("Alberta") is read in any case. A two letter code is read only when it was
 * written in capitals and stands on its own, so "Edmonton AB" and "Austin, TX"
 * resolve and "me" or "in" in a sentence do not.
 */

/** Canadian provinces and territories, postal code to name. */
export const CA_REGIONS: Readonly<Record<string, string>> = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
}

/** US states and DC, postal code to name. */
export const US_REGIONS: Readonly<Record<string, string>> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

const TABLES: Readonly<Record<string, Readonly<Record<string, string>>>> = { CA: CA_REGIONS, US: US_REGIONS }

/** Lowercase, accents folded, punctuation to spaces. "Québec" reads as "quebec". */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Folded full names, longest first, so "west virginia" wins over "virginia". */
const NAMES: readonly { name: string; country: string; code: string }[] = Object.entries(TABLES)
  .flatMap(([country, table]) =>
    Object.entries(table).map(([code, name]) => ({ name: fold(name), country, code })),
  )
  .concat([
    { name: 'newfoundland', country: 'CA', code: 'NL' },
    { name: 'labrador', country: 'CA', code: 'NL' },
    { name: 'pei', country: 'CA', code: 'PE' },
    { name: 'yukon territory', country: 'CA', code: 'YT' },
    { name: 'washington dc', country: 'US', code: 'DC' },
  ])
  .sort((a, b) => b.name.length - a.name.length)

export interface RegionReading {
  /** ISO 3166-1 alpha-2 of the country the region belongs to. */
  country: string
  /** The region's postal code, such as AB or TX. */
  region: string
}

/**
 * The state or province a free-text location names, with its country, or null.
 *
 * `country` is what is already known about the person. When it is set and is
 * neither CA nor US the answer is null, and when it is CA or US only that
 * country's table is read, which is what keeps "Ontario, CA" Canadian.
 */
export function regionFromLocation(
  location: string | null | undefined,
  country?: string | null,
): RegionReading | null {
  const raw = (location ?? '').trim()
  if (raw === '') return null
  const known = (country ?? '').trim().toUpperCase()
  if (known !== '' && TABLES[known] === undefined) return null
  const allowed = (candidate: string) => known === '' || candidate === known

  // Full names first, at word boundaries. "Washington" alone is a state, but
  // "Washington Road" in a street address is not, so a name must fill a whole
  // comma separated part or end it ("Edmonton Alberta").
  const parts = raw
    .split(/[,/|;()\n]/)
    .map(fold)
    .filter((part) => part !== '')
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index] ?? ''
    for (const entry of NAMES) {
      if (!allowed(entry.country)) continue
      if (part === entry.name || part.endsWith(` ${entry.name}`)) return { country: entry.country, region: entry.code }
    }
  }

  // Then a code written in capitals on its own, last one first. A code that
  // both tables could hold does not exist, but "CA" is both California and
  // Canada, so it counts as California only when the country is already US.
  const codes = raw.match(/(?<![A-Za-z])[A-Z]{2}(?![A-Za-z])/g) ?? []
  for (let index = codes.length - 1; index >= 0; index -= 1) {
    const code = codes[index] ?? ''
    if (code === 'CA' && known !== 'US') continue
    for (const [candidate, table] of Object.entries(TABLES)) {
      if (allowed(candidate) && table[code] !== undefined) return { country: candidate, region: code }
    }
  }
  return null
}

/** The display name of a region code, "AB" to "Alberta", or null for a code nobody knows. */
export function regionName(code: string | null | undefined): string | null {
  const key = (code ?? '').trim().toUpperCase()
  return CA_REGIONS[key] ?? US_REGIONS[key] ?? null
}

/** The most regions one filter may name. */
export const REGION_FILTER_MAX = 20

/**
 * A state filter as typed or linked, "AB, BC" or "Alberta, Ontario", read as
 * codes. Anything that names no state or province is refused by name rather
 * than dropped, the way the country filter refuses "USA".
 */
export function parseRegionFilter(raw: string): { codes: string[] } | { error: string } {
  const parts = raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
  if (parts.length === 0) return { codes: [] }
  if (parts.length > REGION_FILTER_MAX) return { error: `at most ${REGION_FILTER_MAX} states or provinces` }
  const codes: string[] = []
  for (const part of parts) {
    // A part may hold several codes separated by spaces, "AB BC".
    const words = part.split(/\s+/)
    const asCodes = words.every((word) => /^[A-Za-z]{2}$/.test(word) && regionName(word) !== null)
    const found = asCodes
      ? words.map((word) => word.toUpperCase())
      : (() => {
          const named = NAMES.find((entry) => entry.name === fold(part))
          return named === undefined ? [] : [named.code]
        })()
    if (found.length === 0) {
      return { error: `${JSON.stringify(part)} is not a US state or Canadian province. Use codes such as AB, ON or TX` }
    }
    for (const code of found) if (!codes.includes(code)) codes.push(code)
  }
  return { codes }
}
